import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Save, Loader2, Plus, Trash2, PlayCircle, X, Sparkles,
  Calculator, CheckCircle2, AlertTriangle, Lock, ClipboardCheck, MessageSquareWarning,
} from "lucide-react";
import { KPI_TEMPLATES as TEMPLATES, FIELD_TYPES, OUTPUT_UNITS, extractVars, computeLocal } from "@/lib/kpiFormula";
import { Switch } from "@/components/ui/switch";

const FREQUENCIES = ["diaria", "semanal", "mensual"];

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-4 py-2 text-sm font-semibold rounded-lg transition-colors ${active ? "bg-[#1A1A1A] text-white" : "text-slate-600 hover:bg-slate-100"}`}
    >
      {children}
    </button>
  );
}

function Labeled({ label, children, hint }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">{label}</label>
      {children}
      {hint && <p className="text-[11px] text-slate-400 mt-0.5">{hint}</p>}
    </div>
  );
}

export default function OpsKpiEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState("info");
  const [kpi, setKpi] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const isMasterLocked = !!(kpi?.is_master && kpi?.catalog_frozen);

  // Asignación
  const [assign, setAssign] = useState({ scope: "any_corporate_admin", department: "", role: "" });
  const [assignSaving, setAssignSaving] = useState(false);

  // Probar fórmula
  const [testOpen, setTestOpen] = useState(false);
  const [testInputs, setTestInputs] = useState({});
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/ops/kpis/definitions/${id}`);
      const d = res.data;
      setKpi({
        ...d,
        input_fields: d.input_fields || [],
        thresholds: d.thresholds || { verde: ">=95", amarillo: ">=80", rojo: "<80" },
      });
      const ra = d.responsible_assignment || {};
      setAssign({ scope: ra.scope || "any_corporate_admin", department: ra.department || "", role: ra.role || "" });
    } catch {
      toast.error("No se pudo cargar el KPI");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const upd = (f, v) => setKpi((p) => ({ ...p, [f]: v }));
  const updThreshold = (k, v) => setKpi((p) => ({ ...p, thresholds: { ...p.thresholds, [k]: v } }));

  const inputKeys = useMemo(() => (kpi?.input_fields || []).map((f) => f.key).filter(Boolean), [kpi]);
  const formulaVars = useMemo(() => extractVars(kpi?.formula_template), [kpi?.formula_template]);
  const missingVars = useMemo(() => formulaVars.filter((v) => !inputKeys.includes(v)), [formulaVars, inputKeys]);

  const applyTemplate = (tpl) => {
    setKpi((p) => ({
      ...p,
      formula_enabled: true,
      formula_template: tpl.formula_template,
      formula_human: tpl.formula_human,
      input_fields: tpl.input_fields.map((f) => ({ ...f })),
      output_unit: tpl.output_unit,
      output_decimals: tpl.output_decimals,
      data_source: p.data_source || tpl.data_source,
      capture_example: p.capture_example || tpl.capture_example,
      description: (p.description || "").trim() ? p.description : tpl.description,
      formula_is_template_suggestion: false,
    }));
    toast.success(`Plantilla aplicada: ${tpl.label}`);
  };

  const addInputField = () =>
    setKpi((p) => ({ ...p, input_fields: [...(p.input_fields || []), { key: "", label: "", type: "number", unit: "", required: true, min: null, max: null, help_text: "" }] }));
  const updInputField = (idx, f, v) =>
    setKpi((p) => ({ ...p, input_fields: p.input_fields.map((x, i) => (i === idx ? { ...x, [f]: v } : x)) }));
  const removeInputField = (idx) =>
    setKpi((p) => ({ ...p, input_fields: p.input_fields.filter((_, i) => i !== idx) }));

  const handleSave = async () => {
    if (kpi.formula_enabled) {
      if (!inputKeys.length) { toast.error("Define al menos un campo de entrada"); setTab("formula"); return; }
      if (!(kpi.formula_template || "").trim()) { toast.error("Escribe la fórmula"); setTab("formula"); return; }
      if (missingVars.length) { toast.error(`La fórmula usa variables no declaradas: ${missingVars.join(", ")}`); setTab("formula"); return; }
    }
    setSaving(true);
    try {
      const payload = {
        description: kpi.description || "",
        thresholds: kpi.thresholds,
        active: kpi.active,
        data_source: kpi.data_source || "",
        capture_example: kpi.capture_example || "",
        help_video_url: kpi.help_video_url || null,
        formula_enabled: !!kpi.formula_enabled,
        formula_template: kpi.formula_enabled ? kpi.formula_template : null,
        formula_human: kpi.formula_human || "",
        input_fields: kpi.formula_enabled ? kpi.input_fields : [],
        output_unit: kpi.output_unit || "percentage",
        output_decimals: parseInt(kpi.output_decimals) ?? 2,
        target_value: kpi.target_value !== undefined && kpi.target_value !== "" && kpi.target_value !== null ? parseFloat(kpi.target_value) : null,
        acceptable_min: kpi.acceptable_min !== undefined && kpi.acceptable_min !== "" && kpi.acceptable_min !== null ? parseFloat(kpi.acceptable_min) : null,
        acceptable_max: kpi.acceptable_max !== undefined && kpi.acceptable_max !== "" && kpi.acceptable_max !== null ? parseFloat(kpi.acceptable_max) : null,
        score_direction: kpi.score_direction || null,
        perspective: kpi.perspective || null,
        manual_capture: kpi.manual_capture !== false,
      };
      if (!isMasterLocked) {
        payload.target = parseFloat(kpi.target);
        payload.frequency = kpi.frequency;
        payload.weight = parseInt(kpi.weight) || 0;
        payload.weight_pct = kpi.weight_pct != null ? parseInt(kpi.weight_pct) : null;
      }
      await api.put(`/ops/kpis/definitions/${id}`, payload);
      toast.success("KPI guardado");
      navigate(kpi.is_master ? "/ops/admin/kpis/catalogo" : "/ops/admin/kpis");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAssignment = async () => {
    setAssignSaving(true);
    try {
      await api.put(`/ops/kpis/definitions/${id}/assignment`, {
        scope: assign.scope,
        department: assign.department || null,
        role: assign.role || null,
      });
      toast.success("Responsable actualizado");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar asignación");
    } finally {
      setAssignSaving(false);
    }
  };

  const runTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await api.post(`/ops/kpis/definitions/${id}/test-formula`, {
        formula: kpi.formula_template,
        input_fields: kpi.input_fields,
        inputs: Object.fromEntries(Object.entries(testInputs).map(([k, v]) => [k, Number(v)])),
      });
      setTestResult({ ok: true, value: res.data.calculated_value });
    } catch (e) {
      setTestResult({ ok: false, error: e.response?.data?.detail || "Error" });
    } finally {
      setTesting(false);
    }
  };

  const livePreview = useMemo(() => {
    if (!testOpen) return null;
    const filled = {};
    for (const f of kpi?.input_fields || []) {
      if (testInputs[f.key] === undefined || testInputs[f.key] === "") return null;
      filled[f.key] = testInputs[f.key];
    }
    return computeLocal(kpi?.formula_template, filled);
  }, [testOpen, testInputs, kpi]);

  if (loading) {
    return <div className="p-6 flex items-center gap-2 text-slate-500"><Loader2 className="w-5 h-5 animate-spin" /> Cargando KPI…</div>;
  }
  if (!kpi) return <div className="p-6 text-slate-500">KPI no encontrado.</div>;

  return (
    <div className="p-4 sm:p-6 max-w-3xl mx-auto space-y-5">
      <button onClick={() => navigate(kpi.is_master ? "/ops/admin/kpis/catalogo" : "/ops/admin/kpis")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800" data-testid="kpi-editor-back">
        <ArrowLeft className="w-4 h-4" /> Volver {kpi.is_master ? "al catálogo" : "a definiciones"}
      </button>

      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2" data-testid="kpi-editor-title">
          {kpi.name}
          {kpi.config_status === "pending_confirmation" && (
            <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full border bg-blue-50 text-blue-700 border-blue-200" data-testid="kpi-editor-pending-confirmation-badge">
              <ClipboardCheck className="w-3 h-3" /> Por confirmar
            </span>
          )}
        </h1>
        <p className="text-sm text-slate-500">{kpi.area} · {kpi.frequency} · meta {kpi.target_display || kpi.target} {kpi.unit}</p>
      </div>

      {kpi.config_status === "pending_confirmation" && kpi.config_notes && (
        <div className="flex items-start gap-2 text-sm bg-blue-50 border border-blue-200 text-blue-800 rounded-xl px-4 py-3" data-testid="kpi-editor-config-notes-banner">
          <MessageSquareWarning className="w-4 h-4 mt-0.5 shrink-0" />
          <span><strong>Propuesta por confirmar:</strong> {kpi.config_notes}</span>
        </div>
      )}

      {kpi.is_master && kpi.category && (
        <div className="flex items-start gap-2 text-sm bg-slate-900 text-white rounded-xl px-4 py-3" data-testid="kpi-master-bonus-banner">
          <Lock className="w-4 h-4 mt-0.5 shrink-0 text-[#FFDF00]" />
          <span>
            Este KPI pertenece a <strong>{kpi.category.name}</strong> —{" "}
            {kpi.category.bonus_config?.amount_type === "fixed"
              ? `bono $${kpi.category.bonus_config?.amount_mxn} MXN ${kpi.category.bonus_config?.frequency === "weekly" ? "semanal" : "mensual"}`
              : `bono ${kpi.category.bonus_config?.percentage ? kpi.category.bonus_config.percentage + "% del salario" : "% del salario"} ${kpi.category.bonus_config?.frequency === "weekly" ? "semanal" : "mensual"}`}
            {" "}proporcional al resultado del puesto.
            {isMasterLocked && <> Meta, frecuencia y ponderación están bloqueadas por el catálogo maestro.</>}
          </span>
        </div>
      )}

      {kpi.formula_is_template_suggestion && (
        <div className="flex items-start gap-2 text-sm bg-violet-50 border border-violet-200 text-violet-800 rounded-xl px-4 py-3" data-testid="kpi-template-suggestion-banner">
          <Sparkles className="w-4 h-4 mt-0.5 shrink-0" />
          <span><strong>Plantilla sugerida.</strong> Pre-cargamos una fórmula de ejemplo para este KPI. Revísala y ajústala a tu operación; al guardar quedará confirmada.</span>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-50 rounded-xl p-1 w-fit">
        <TabBtn active={tab === "info"} onClick={() => setTab("info")} testid="kpi-tab-info">Información</TabBtn>
        <TabBtn active={tab === "formula"} onClick={() => setTab("formula")} testid="kpi-tab-formula">Fórmula y cálculo</TabBtn>
        <TabBtn active={tab === "assign"} onClick={() => setTab("assign")} testid="kpi-tab-assign">Asignación</TabBtn>
      </div>

      {/* TAB INFORMACIÓN */}
      {tab === "info" && (
        <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-4" data-testid="kpi-tab-panel-info">
          <Labeled label="Descripción del KPI" hint="Qué mide, en lenguaje claro para el responsable.">
            <textarea value={kpi.description || ""} onChange={(e) => upd("description", e.target.value)} rows={3} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00] resize-none" data-testid="kpi-desc-input" />
          </Labeled>
          <Labeled label="Origen de la información" hint="De dónde sacar los datos.">
            <textarea value={kpi.data_source || ""} onChange={(e) => upd("data_source", e.target.value)} rows={2} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00] resize-none" data-testid="kpi-source-input" />
          </Labeled>
          <Labeled label="Ejemplo de captura">
            <textarea value={kpi.capture_example || ""} onChange={(e) => upd("capture_example", e.target.value)} rows={2} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00] resize-none" data-testid="kpi-example-input" />
          </Labeled>
          <Labeled label="URL del video tutorial (opcional)">
            <input value={kpi.help_video_url || ""} onChange={(e) => upd("help_video_url", e.target.value)} placeholder="https://www.youtube.com/watch?v=..." className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]" data-testid="kpi-video-input" />
          </Labeled>
          <div className="grid grid-cols-3 gap-4">
            <Labeled label={isMasterLocked ? "Meta (bloqueada)" : "Meta"}>
              <input
                type="number" value={kpi.target ?? ""} onChange={(e) => upd("target", e.target.value)}
                disabled={isMasterLocked}
                className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00] ${isMasterLocked ? "bg-slate-100 text-slate-400 cursor-not-allowed border-slate-200" : "border-slate-200"}`}
                data-testid="kpi-target-input"
              />
            </Labeled>
            <Labeled label={isMasterLocked ? "Frecuencia (bloqueada)" : "Frecuencia"}>
              <select
                value={kpi.frequency} onChange={(e) => upd("frequency", e.target.value)}
                disabled={isMasterLocked}
                className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00] ${isMasterLocked ? "bg-slate-100 text-slate-400 cursor-not-allowed border-slate-200" : "border-slate-200"}`}
                data-testid="kpi-frequency-select"
              >
                {FREQUENCIES.map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
            </Labeled>
            {kpi.is_master && (
              <Labeled label={isMasterLocked ? "Ponderación % (bloqueada)" : "Ponderación %"}>
                <input
                  type="number" value={kpi.weight_pct ?? ""} onChange={(e) => upd("weight_pct", e.target.value)}
                  disabled={isMasterLocked}
                  className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00] ${isMasterLocked ? "bg-slate-100 text-slate-400 cursor-not-allowed border-slate-200" : "border-slate-200"}`}
                  data-testid="kpi-weight-pct-input"
                />
              </Labeled>
            )}
          </div>
          <Labeled label="Perspectiva del Balanced Scorecard" hint="Financiero, Cliente, Proceso o Aprendizaje y Crecimiento.">
            <select
              value={kpi.perspective || ""} onChange={(e) => upd("perspective", e.target.value || null)}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]"
              data-testid="kpi-perspective-select"
            >
              <option value="">Sin asignar</option>
              <option value="financiero">Financiero</option>
              <option value="cliente">Cliente</option>
              <option value="proceso">Proceso</option>
              <option value="aprendizaje">Aprendizaje y Crecimiento</option>
            </select>
          </Labeled>
          <Labeled label="Fuente del resultado" hint="Automático: el sistema puebla el valor (ventas/checklists). Manual: lo captura únicamente RH.">
            <select
              value={kpi.manual_capture === false ? "auto" : "manual"}
              onChange={(e) => upd("manual_capture", e.target.value === "manual")}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]"
              data-testid="kpi-fuente-select"
            >
              <option value="manual">Captura RH (manual)</option>
              <option value="auto">Automático (lo puebla el sistema)</option>
            </select>
          </Labeled>
          <Labeled label="Umbrales semáforo" hint="Operadores: >= <= > < ==">
            <div className="grid grid-cols-3 gap-2">
              {[["verde", "text-green-700"], ["amarillo", "text-yellow-600"], ["rojo", "text-red-600"]].map(([k, cls]) => (
                <div key={k}>
                  <label className={`block text-xs font-semibold ${cls} mb-1 capitalize`}>{k}</label>
                  <input value={kpi.thresholds?.[k] || ""} onChange={(e) => updThreshold(k, e.target.value)} className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-[#FFDF00] font-mono" placeholder=">=95" />
                </div>
              ))}
            </div>
          </Labeled>
          <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
            <input type="checkbox" checked={!!kpi.active} onChange={(e) => upd("active", e.target.checked)} className="w-4 h-4 accent-[#FFDF00]" /> KPI activo
          </label>
        </div>
      )}

      {/* TAB FÓRMULA */}
      {tab === "formula" && (
        <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-4" data-testid="kpi-tab-panel-formula">
          <div className="flex items-center justify-between gap-3 bg-slate-50 rounded-xl px-4 py-3">
            <span className="flex items-center gap-2 text-sm font-semibold text-slate-800">
              <Calculator className="w-4 h-4" /> Este KPI se calcula automáticamente con fórmula
            </span>
            <Switch checked={!!kpi.formula_enabled} onCheckedChange={(v) => upd("formula_enabled", v)} data-testid="kpi-formula-toggle" />
          </div>

          {kpi.formula_enabled && (
            <div className="space-y-4">
              {/* Plantillas rápidas */}
              <div>
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">¿Quieres usar una plantilla?</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {TEMPLATES.map((t) => (
                    <button
                      key={t.key}
                      onClick={() => applyTemplate(t)}
                      className="text-left border border-slate-200 rounded-xl px-3 py-2 hover:border-[#FFDF00] hover:bg-yellow-50 transition-colors"
                      data-testid={`kpi-template-${t.key}`}
                    >
                      <div className="text-sm font-semibold text-slate-800">{t.label}</div>
                      <div className="text-[11px] text-slate-400 font-mono mt-0.5">{t.formula_template}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Campos de entrada */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Campos de entrada (datos crudos)</span>
                  <button onClick={addInputField} className="inline-flex items-center gap-1 text-xs font-semibold text-slate-700 hover:text-black" data-testid="kpi-add-input-field">
                    <Plus className="w-3.5 h-3.5" /> Agregar campo
                  </button>
                </div>
                <div className="space-y-3">
                  {(kpi.input_fields || []).map((f, idx) => (
                    <div key={idx} className="border border-slate-200 rounded-xl p-3 space-y-2" data-testid={`kpi-input-row-${idx}`}>
                      <div className="grid grid-cols-2 gap-2">
                        <input value={f.key} onChange={(e) => updInputField(idx, "key", e.target.value.replace(/\s+/g, "_"))} placeholder="key (ej. ventas_real)" className="border border-slate-200 rounded-lg px-2 py-1.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#FFDF00]" data-testid={`kpi-input-key-${idx}`} />
                        <input value={f.label} onChange={(e) => updInputField(idx, "label", e.target.value)} placeholder="Etiqueta visible" className="border border-slate-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]" data-testid={`kpi-input-label-${idx}`} />
                      </div>
                      <div className="grid grid-cols-4 gap-2">
                        <select value={f.type || "number"} onChange={(e) => updInputField(idx, "type", e.target.value)} className="border border-slate-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-[#FFDF00]">
                          {FIELD_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                        <input value={f.unit || ""} onChange={(e) => updInputField(idx, "unit", e.target.value || null)} placeholder="unidad" className="border border-slate-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-[#FFDF00]" />
                        <input type="number" value={f.min ?? ""} onChange={(e) => updInputField(idx, "min", e.target.value === "" ? null : parseFloat(e.target.value))} placeholder="min" className="border border-slate-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-[#FFDF00]" />
                        <input type="number" value={f.max ?? ""} onChange={(e) => updInputField(idx, "max", e.target.value === "" ? null : parseFloat(e.target.value))} placeholder="max" className="border border-slate-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-[#FFDF00]" />
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <input value={f.help_text || ""} onChange={(e) => updInputField(idx, "help_text", e.target.value)} placeholder="Ayuda (opcional)" className="flex-1 border border-slate-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-[#FFDF00]" />
                        <label className="flex items-center gap-1 text-xs text-slate-600 select-none whitespace-nowrap">
                          <input type="checkbox" checked={f.required !== false} onChange={(e) => updInputField(idx, "required", e.target.checked)} className="w-3.5 h-3.5 accent-[#FFDF00]" /> Obligatorio
                        </label>
                        <button onClick={() => removeInputField(idx)} className="p-1.5 rounded-lg text-red-400 hover:bg-red-50" data-testid={`kpi-remove-input-${idx}`}><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    </div>
                  ))}
                  {(kpi.input_fields || []).length === 0 && <p className="text-xs text-slate-400">Agrega los campos que el responsable va a capturar.</p>}
                </div>
              </div>

              {/* Fórmula */}
              <Labeled label="Fórmula (usa las keys de arriba)" hint="Operadores: + - * / % ** ( ). Funciones: min, max, round, abs, sum, avg.">
                <input value={kpi.formula_template || ""} onChange={(e) => upd("formula_template", e.target.value)} placeholder="(ventas_real / meta_mes) * 100" className={`w-full border rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#FFDF00] ${missingVars.length ? "border-red-300" : "border-slate-200"}`} data-testid="kpi-formula-input" />
              </Labeled>
              {missingVars.length > 0 && (
                <p className="text-xs text-red-600 flex items-center gap-1" data-testid="kpi-formula-error">
                  <AlertTriangle className="w-3.5 h-3.5" /> Variables no declaradas en los campos: {missingVars.join(", ")}
                </p>
              )}
              <Labeled label="Fórmula legible (lo que ve el usuario)">
                <input value={kpi.formula_human || ""} onChange={(e) => upd("formula_human", e.target.value)} placeholder="(Ventas reales ÷ Meta del período) × 100" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]" data-testid="kpi-formula-human-input" />
              </Labeled>

              <div className="grid grid-cols-2 gap-4">
                <Labeled label="Unidad del resultado">
                  <select value={kpi.output_unit || "percentage"} onChange={(e) => upd("output_unit", e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]" data-testid="kpi-output-unit">
                    {OUTPUT_UNITS.map((u) => <option key={u.v} value={u.v}>{u.l}</option>)}
                  </select>
                </Labeled>
                <Labeled label="Decimales"><input type="number" min={0} max={4} value={kpi.output_decimals ?? 2} onChange={(e) => upd("output_decimals", e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]" /></Labeled>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <Labeled label="Meta esperada"><input type="number" value={kpi.target_value ?? ""} onChange={(e) => upd("target_value", e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]" data-testid="kpi-target-value-input" /></Labeled>
                <Labeled label="Mínimo aceptable"><input type="number" value={kpi.acceptable_min ?? ""} onChange={(e) => upd("acceptable_min", e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]" data-testid="kpi-acceptable-min-input" /></Labeled>
                <Labeled label="Máximo aceptable"><input type="number" value={kpi.acceptable_max ?? ""} onChange={(e) => upd("acceptable_max", e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]" data-testid="kpi-acceptable-max-input" /></Labeled>
              </div>
              <Labeled label="Dirección del score" hint="¿Un valor mayor es mejor (ej. ventas) o menor es mejor (ej. merma, errores, tiempos)?">
                <select value={kpi.score_direction || "higher_is_better"} onChange={(e) => upd("score_direction", e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]" data-testid="kpi-score-direction-select">
                  <option value="higher_is_better">Mayor es mejor</option>
                  <option value="lower_is_better">Menor es mejor</option>
                </select>
              </Labeled>

              <button
                onClick={() => { setTestInputs({}); setTestResult(null); setTestOpen(true); }}
                disabled={!inputKeys.length || !(kpi.formula_template || "").trim()}
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-blue-600 hover:text-blue-800 disabled:opacity-40"
                data-testid="kpi-test-formula-btn"
              >
                <PlayCircle className="w-4 h-4" /> Probar fórmula
              </button>
            </div>
          )}
        </div>
      )}

      {/* TAB ASIGNACIÓN */}
      {tab === "assign" && (
        <div className="bg-white border border-slate-200 rounded-2xl p-5 space-y-4 max-w-md" data-testid="kpi-tab-panel-assign">
          <Labeled label="Tipo de responsable">
            <select value={assign.scope} onChange={(e) => setAssign((p) => ({ ...p, scope: e.target.value }))} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]" data-testid="kpi-assign-scope">
              <option value="branch">Branch manager (por sucursal)</option>
              <option value="corporate_dept">Corporativo por departamento</option>
              <option value="corporate_role">Corporativo por rol</option>
              <option value="any_corporate_admin">Cualquier corp admin</option>
            </select>
          </Labeled>
          {assign.scope === "corporate_dept" && (
            <Labeled label="Departamento">
              <select value={assign.department} onChange={(e) => setAssign((p) => ({ ...p, department: e.target.value }))} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]">
                <option value="">Seleccionar...</option>
                {["RH", "Compras", "Operaciones", "Dirección", "Legal", "Tesorería", "Diseño"].map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </Labeled>
          )}
          {assign.scope === "corporate_role" && (
            <Labeled label="Rol">
              <select value={assign.role} onChange={(e) => setAssign((p) => ({ ...p, role: e.target.value }))} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]">
                <option value="">Seleccionar...</option>
                {["treasury", "legal", "branch_manager", "corporate_admin", "hr_admin"].map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </Labeled>
          )}
          <button onClick={handleSaveAssignment} disabled={assignSaving} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#FFDF00] font-semibold text-sm text-slate-900 hover:bg-yellow-300 transition-colors disabled:opacity-50" data-testid="kpi-save-assignment-btn">
            {assignSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar asignación
          </button>
        </div>
      )}

      {/* Footer guardar (info + formula) */}
      {tab !== "assign" && (
        <div className="flex gap-3">
          <button onClick={() => navigate("/ops/admin/kpis")} className="flex-1 py-3 rounded-xl border border-slate-200 font-semibold text-slate-600 hover:bg-slate-50 transition-colors">Cancelar</button>
          <button onClick={handleSave} disabled={saving} className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-[#1A1A1A] font-semibold text-white hover:bg-black transition-colors disabled:opacity-50" data-testid="kpi-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar KPI
          </button>
        </div>
      )}

      {/* Modal probar fórmula */}
      {testOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-5 space-y-4 shadow-2xl">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-slate-900">Probar fórmula</h3>
              <button onClick={() => setTestOpen(false)} className="p-2 rounded-lg hover:bg-slate-100"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-xs text-slate-500 font-mono bg-slate-50 rounded-lg px-3 py-2">{kpi.formula_template}</p>
            <div className="space-y-2">
              {(kpi.input_fields || []).map((f) => (
                <div key={f.key}>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">{f.label || f.key}</label>
                  <input type="number" value={testInputs[f.key] ?? ""} onChange={(e) => setTestInputs((p) => ({ ...p, [f.key]: e.target.value }))} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]" data-testid={`kpi-test-input-${f.key}`} />
                </div>
              ))}
            </div>
            {livePreview !== null && <div className="text-sm text-slate-500">Vista previa local: <span className="font-bold text-slate-800">{livePreview}</span></div>}
            <button onClick={runTest} disabled={testing} className="w-full py-2.5 rounded-xl bg-[#FFDF00] font-semibold text-sm text-slate-900 hover:bg-yellow-300 transition-colors disabled:opacity-50 flex items-center justify-center gap-2" data-testid="kpi-run-test-btn">
              {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />} Calcular
            </button>
            {testResult && (
              <div className={`rounded-xl px-4 py-3 text-sm ${testResult.ok ? "bg-emerald-50 text-emerald-800" : "bg-red-50 text-red-700"}`} data-testid="kpi-test-result">
                {testResult.ok ? (
                  <span className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> Resultado: <strong>{testResult.value}</strong></span>
                ) : (
                  <span className="flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> {testResult.error}</span>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
